import { Button } from "@package/subframe/components/Button";
import { TextArea } from "@package/subframe/components/TextArea";
import { useState } from "react";

import type { ChecklistResultValue } from "@/hooks/use-offline-compliance-completion";

interface Props {
  itemId: string;
  label: string;
  result?: ChecklistResultValue;
  notes?: string;
  onResultChange: (
    itemId: string,
    result: ChecklistResultValue,
    notes?: string,
  ) => void;
}

const RESULT_OPTIONS: Array<{
  value: ChecklistResultValue;
  label: string;
  // Variant derived from Subframe's Button system.
  variant: "neutral-primary" | "destructive-primary" | "neutral-secondary";
}> = [
  { value: "PASS", label: "Pass", variant: "neutral-primary" },
  { value: "FAIL", label: "Fail", variant: "destructive-primary" },
  // N/A intentionally rendered with the lightest emphasis to reduce
  // accidental selection — UX designer recommendation.
  { value: "NOT_APPLICABLE", label: "N/A", variant: "neutral-secondary" },
];

export function ChecklistItemRow({
  itemId,
  label,
  result,
  notes,
  onResultChange,
}: Props) {
  // Reveal the notes textarea when FAIL or N/A is selected — pass clears it.
  const [localNotes, setLocalNotes] = useState(notes ?? "");
  const showNotes = result === "FAIL" || result === "NOT_APPLICABLE";

  const handleSelect = (value: ChecklistResultValue) => {
    onResultChange(itemId, value, value === "PASS" ? undefined : localNotes);
  };

  const handleNotesChange = (text: string) => {
    setLocalNotes(text);
    if (result) {
      onResultChange(itemId, result, text);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-3 rounded-md border border-neutral-border p-4">
      <span className="text-body-bold font-body-bold text-neutral-900">
        {label}
      </span>
      <div className="flex w-full items-center gap-2" role="radiogroup">
        {RESULT_OPTIONS.map((option) => {
          const isSelected = result === option.value;
          return (
            <Button
              key={option.value}
              variant={isSelected ? option.variant : "neutral-secondary"}
              onClick={() => handleSelect(option.value)}
              aria-pressed={isSelected}
            >
              {option.label}
            </Button>
          );
        })}
      </div>
      {showNotes && (
        <TextArea className="h-auto w-full" label="" helpText="">
          <TextArea.Input
            placeholder={
              result === "FAIL"
                ? "Optional — what's wrong?"
                : "Optional — why doesn't this apply?"
            }
            value={localNotes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleNotesChange(e.target.value)
            }
          />
        </TextArea>
      )}
    </div>
  );
}
